import type { FC, ReactNode } from 'react'

import { useState } from 'react'

import {
    Bed,
    Clock,
    Info,
    Sunrise
} from 'lucide-react'

import { cn } from '@/lib/utils'

// A cycle is modelled at 90 minutes: the midpoint of the 80 to 100 minute range
// NHLBI describes. It is a planning interval, not a measured constant, and the
// article body says so rather than implying the number is precise.
const CYCLE_MINUTES = 90

// NHLBI: "Usually there are four to six cycles per night." Seven is offered for
// long sleepers but sits outside that stated range.
const CYCLE_OPTIONS = [4, 5, 6, 7]

// CDC/AASM: adults aged 18 to 60 are recommended at least 7 hours a night.
const RECOMMENDED_MINUTES = 7 * 60

// Deliberately round numbers. There is no authoritative figure for average
// sleep-onset latency in healthy adults, so this is an adjustable allowance
// rather than a research-derived average.
const LATENCY_OPTIONS = [0, 10, 15, 20, 30, 45]

type Mode = 'wake' | 'bed'

interface Suggestion {
    cycles: number
    minutes: number
    sleepMinutes: number
    meetsGuidance: boolean
}

const parseTime = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return hours * 60 + minutes
}

const formatClock = (total: number): string => {
    const wrapped = ((total % 1440) + 1440) % 1440
    const hours24 = Math.floor(wrapped / 60)
    const minutes = wrapped % 60
    const suffix = hours24 < 12 ? 'am' : 'pm'
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12

    return `${hours12}:${String(minutes).padStart(2, '0')} ${suffix}`
}

const formatDuration = (total: number): string => {
    const hours = Math.floor(total / 60)
    const minutes = total % 60

    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

const buildSuggestions = (mode: Mode, anchor: number, latency: number): Suggestion[] => {
    const ordered = mode === 'wake' ? [...CYCLE_OPTIONS].reverse() : CYCLE_OPTIONS

    return ordered.map((cycles) => {
        const sleepMinutes = cycles * CYCLE_MINUTES
        const minutes =
            mode === 'wake'
                ? anchor - latency - sleepMinutes
                : anchor + latency + sleepMinutes

        return {
            cycles,
            minutes,
            sleepMinutes,
            meetsGuidance: sleepMinutes >= RECOMMENDED_MINUTES
        }
    })
}

export const BedtimeCalculator: FC = (): ReactNode => {
    const [mode, setMode] = useState<Mode>('wake')
    const [wakeTime, setWakeTime] = useState('07:00')
    const [bedTime, setBedTime] = useState('22:30')
    const [latency, setLatency] = useState(15)

    const value = mode === 'wake' ? wakeTime : bedTime
    const anchor = parseTime(value)
    const suggestions = anchor === null ? [] : buildSuggestions(mode, anchor, latency)

    return (
        <section
            aria-label='Bedtime calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Clock className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Sleep cycle calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Work backward from your alarm, or forward from your bedtime.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div
                    role='group'
                    aria-label='Calculation mode'
                    className='grid grid-cols-1 gap-2 sm:grid-cols-2'
                >
                    <button
                        type='button'
                        aria-pressed={mode === 'wake'}
                        onClick={() => setMode('wake')}
                        className={cn(
                            'flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                            mode === 'wake'
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:bg-secondary'
                        )}
                    >
                        <Sunrise className='size-4 shrink-0' />
                        I need to wake up at
                    </button>

                    <button
                        type='button'
                        aria-pressed={mode === 'bed'}
                        onClick={() => setMode('bed')}
                        className={cn(
                            'flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                            mode === 'bed'
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:bg-secondary'
                        )}
                    >
                        <Bed className='size-4 shrink-0' />
                        I am going to bed at
                    </button>
                </div>

                <div className='mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    <div>
                        <label
                            htmlFor='bedtime-calculator-time'
                            className='block text-sm font-medium text-foreground'
                        >
                            {mode === 'wake' ? 'Wake-up time' : 'Bedtime'}
                        </label>
                        <input
                            id='bedtime-calculator-time'
                            type='time'
                            value={value}
                            onChange={(event) =>
                                mode === 'wake'
                                    ? setWakeTime(event.target.value)
                                    : setBedTime(event.target.value)
                            }
                            className='mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        />
                    </div>

                    <div>
                        <label
                            htmlFor='bedtime-calculator-latency'
                            className='block text-sm font-medium text-foreground'
                        >
                            Allow for falling asleep
                        </label>
                        <select
                            id='bedtime-calculator-latency'
                            value={latency}
                            onChange={(event) => setLatency(Number(event.target.value))}
                            className='mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        >
                            {LATENCY_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {option === 0 ? 'No allowance' : `${option} minutes`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className='mt-6'>
                    <span className='block text-sm font-medium text-foreground'>
                        {mode === 'wake'
                            ? 'Try going to bed at one of these times'
                            : 'Try setting your alarm for one of these times'}
                    </span>

                    {anchor === null ? (
                        <span className='mt-3 block text-sm text-muted-foreground'>
                            Enter a time to see suggestions.
                        </span>
                    ) : (
                        <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                            {suggestions.map((item) => (
                                <div
                                    key={item.cycles}
                                    className={cn(
                                        'rounded-xl border px-4 py-3',
                                        item.meetsGuidance
                                            ? 'border-primary/40 bg-primary/5'
                                            : 'border-border bg-background'
                                    )}
                                >
                                    <span className='block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                        {formatClock(item.minutes)}
                                    </span>
                                    <span className='mt-1 block text-sm text-muted-foreground'>
                                        {item.cycles} cycles, {formatDuration(item.sleepMinutes)} of
                                        sleep
                                    </span>
                                    {!item.meetsGuidance && (
                                        <span className='mt-2 block text-xs font-medium text-muted-foreground'>
                                            Below the 7 hour guidance for adults
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        These are estimates. Cycle length varies between people and across the
                        night, so treat the times as a starting point rather than a target to hit
                        exactly.
                    </span>
                </div>
            </div>
        </section>
    )
}

export default BedtimeCalculator
